/** Ámbito de ciudad: el libro cubre varias, pero se lee, se explora y se juega de una en una.
 *  La ciudad vive en la URL (`?ciudad=avila`) como todo lo demás (spec §7: la URL es la fuente de verdad). */
import type { ChapterIndex } from '../data/types';

export interface CityScope {
  /** identificador de colección: `avi-capital`, `seg-capital`… */
  collectionId: string;
  /** prefijo de parte usado en los identificadores: `avi`, `seg`… */
  part: string;
  slug: string;
  city: string;
  province: string;
  label: string;
  /** `capital` o `provincia`: cambia el encuadre del mapa y los rótulos. */
  level: string;
  /** Título de la portada y de la cabecera. */
  title: string;
  /** Nombre del ámbito tal como se usa dentro de una frase («la Segovia», «la provincia de Ávila»). */
  inline: string;
  /** Rótulo de la sección del libro: «Segovia capital», «Provincia de Ávila». */
  section: string;
}

export const slugify = (s: string) =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

/** Las ciudades publicadas, en el orden en que aparecen en el libro. */
export function citiesOf(index: ChapterIndex): CityScope[] {
  const out = new Map<string, CityScope>();
  for (const c of index.chapters) {
    if (out.has(c.collectionId)) continue;
    // los capítulos de provincia no tienen ciudad: su ámbito es la provincia entera
    const name = c.city ?? `Provincia de ${c.province}`;
    out.set(c.collectionId, {
      collectionId: c.collectionId,
      part: c.chapterId.split('-')[0],
      slug: slugify(name),
      city: name,
      province: c.province,
      label: c.city ?? `${c.province} (provincia)`,
      level: c.level,
      title: c.city ? `La ${c.city} de 1884` : `La provincia de ${c.province} en 1884`,
      inline: c.city ? `la ${c.city}` : `la provincia de ${c.province}`,
      section: c.city ? `${c.city} capital` : `Provincia de ${c.province}`,
    });
  }
  return [...out.values()];
}

/** El ámbito por omisión: la última capital del libro (Segovia). Los capítulos de provincia se
 *  publican después de su capital, así que sin este matiz la portada abriría en una provincia. */
export function defaultCity(cities: CityScope[]): CityScope {
  const pool = cities.filter((c) => c.level === 'capital');
  const list = pool.length ? pool : cities;
  return list[list.length - 1];
}

/** La ciudad activa: la del parámetro `ciudad` si existe y está publicada; si no, la de por omisión. */
export function resolveCity(cities: CityScope[], requested: string | null): CityScope {
  return cities.find((c) => c.slug === requested) ?? defaultCity(cities);
}
